import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Modal, Select } from "antd";
import { useNavigate } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useFileUpload } from "../../hooks/useFileUpload";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";
import olreadiaService from "../../services/olreadiaService";
import { ensureGoogleMapsPlacesLoaded } from "../../utils/googleMapsLoader";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const STATUS_OPTIONS = ["CLOCKED_IN", "CLOCKED_OUT"];
const ATTENDANCE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const LOCATION_EXCEPTION_THRESHOLD_MILES = 1;
const CLOCK_PHOTO_MAX_BYTES = 20 * 1024;
const clean = (value) => String(value || "").trim();
const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};
const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};
const userFor = (profile = {}) => profile?.userProfile || profile?.user || {};
const profileFor = (profile = {}) => profile?.profile || profile?.profileData || {};
const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";
const userIdFor = (profile = {}) => {
  const user = userFor(profile);
  return user.id || profile.userProfileId || profile.userId || "";
};
const nameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.name) ||
    clean(base.fullName) ||
    [base.firstName, base.middleName, base.lastName].map(clean).filter(Boolean).join(" ") ||
    clean(profile.name) ||
    "Care staff";
};
const emailFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.email) || clean(base.email) || "";
};
const statusLabel = (value) => String(value || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const formatTimestamp = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const formatDateLabel = (value) => {
  if (!value) return "N/A";
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? parseDateKey(value) : new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};
const formatTimeLabel = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const timestampMs = (value) => {
  if (!value) return NaN;
  if (value instanceof Date) return value.getTime();
  if (Array.isArray(value)) {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  if (typeof value === "number") return value;
  if (typeof value === "object") {
    if (value.timestamp || value.dateTime || value.date || value.value) {
      return timestampMs(value.timestamp || value.dateTime || value.date || value.value);
    }
    const year = value.year;
    if (year != null) {
      const month = value.monthValue ?? value.month ?? 1;
      const day = value.dayOfMonth ?? value.day ?? 1;
      const hour = value.hour ?? 0;
      const minute = value.minute ?? 0;
      const second = value.second ?? 0;
      return new Date(year, month - 1, day, hour, minute, second).getTime();
    }
  }
  const text = String(value);
  let parsed = new Date(text).getTime();
  if (Number.isFinite(parsed)) return parsed;
  parsed = new Date(text.replace(" ", "T")).getTime();
  return parsed;
};
const hoursBetween = (start, end) => {
  const startMs = timestampMs(start);
  const endMs = timestampMs(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 0;
  return Math.max(0, endMs - startMs) / 3600000;
};
const formatHours = (value) => `${Number(value || 0).toFixed(2)} hr${Math.abs(Number(value || 0) - 1) < 0.005 ? "" : "s"}`;
const excelCell = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const filenameDate = () => new Date().toISOString().slice(0, 10);
const timerTimestampMs = (value, now = Date.now()) => {
  if (!value) return NaN;
  const direct = typeof value === "string" ? Date.parse(value) : timestampMs(value);
  if (Number.isFinite(direct) && direct <= now + 5000) return direct;
  if (typeof value !== "string") return direct;

  const text = value.trim().replace(" ", "T");
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(text);
  if (!hasTimezone) {
    const utc = Date.parse(`${text}Z`);
    if (Number.isFinite(utc) && utc <= now + 5000) return utc;
  }
  return direct;
};
function AttendanceElapsedTimer({ startedAt, stoppedAt }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (stoppedAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [stoppedAt]);

  const start = timerTimestampMs(startedAt, now);
  const stop = timerTimestampMs(stoppedAt, now);
  const end = Number.isFinite(stop) ? stop : now;
  const elapsed = Number.isFinite(start) ? Math.max(0, Math.floor((end - start) / 1000)) : 0;
  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  return (
    <span className="attendance-elapsed-timer">
      <span>{String(hours).padStart(2, "0")}</span>
      <b>:</b>
      <span>{String(minutes).padStart(2, "0")}</span>
      <b>:</b>
      <span>{String(seconds).padStart(2, "0")}</span>
    </span>
  );
}
const locationFromInfo = (info = {}) => {
  const source = info?.locationInformation || info?.address || info?.location || info || {};
  const geometryLocation = info?.geometry?.location || source.geometry?.location || {};
  const coordinates =
    info?.coordinates ||
    info?.coords ||
    info?.coordinate ||
    info?.geoLocation ||
    info?.geolocation ||
    source.coordinates ||
    source.coords ||
    source.coordinate ||
    source.geoLocation ||
    source.geolocation ||
    {};
  const id = clean(info?.id || source.id);
  const name = clean(info?.name || source.name);
  const placeId = clean(source.placeId || source.place_id || info?.placeId || info?.place_id);
  const contact = info?.contactInformation || source.contactInformation || {};
  const type = info?.organizationLocationType || info?.businessLocationType || info?.type || source.organizationLocationType || source.businessLocationType || source.type;
  const latitude = Number(
    source.latitude ??
      source.lat ??
      info?.latitude ??
      info?.lat ??
      coordinates.latitude ??
      coordinates.lat ??
      (typeof geometryLocation.lat === "function" ? geometryLocation.lat() : geometryLocation.lat),
  );
  const longitude = Number(
    source.longitude ??
      source.lng ??
      source.lon ??
      info?.longitude ??
      info?.lng ??
      info?.lon ??
      coordinates.longitude ??
      coordinates.lng ??
      coordinates.lon ??
      (typeof geometryLocation.lng === "function" ? geometryLocation.lng() : geometryLocation.lng),
  );
  const address = clean(source.address) ||
    clean(source.formattedAddress) ||
    clean(source.addressLine1) ||
    [source.city, source.state, source.zipCode || source.postalCode].map(clean).filter(Boolean).join(", ");
  const base = {
    organizationLocationId: id || null,
    name,
    type,
    status: clean(info?.status || source.status),
    contactInformation: contact,
    address,
    addressLine1: clean(source.addressLine1),
    addressLine2: clean(source.addressLine2),
    city: clean(source.city),
    county: clean(source.county),
    state: clean(source.state),
    zipCode: clean(source.zipCode || source.postalCode),
    country: clean(source.country),
    placeId: placeId || null,
  };
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    const displayAddress = address || name;
    return displayAddress ? { ...base, address: displayAddress } : null;
  }
  return {
    ...base,
    latitude,
    longitude,
    address: address || name,
  };
};
const locationsOfOrganization = (organization = {}) =>
  organization.businessLocations || organization.organizationLocations || organization.locations || [];
const locationsOfWorkspace = (workspace = {}) => [
  workspace.organizationLocation,
  workspace.businessLocation,
  workspace.selectedOrganizationLocation,
  workspace.selectedBusinessLocation,
  workspace.locationInformation,
  workspace.location,
  workspace.address,
  ...(workspace.businessLocations || []),
  ...(workspace.organizationLocations || []),
  ...(workspace.locations || []),
].filter(Boolean);
const locationFromOrganization = (organization = {}, locations = [], organizationLocationId = "", workspace = {}) => {
  const attachedLocations = [...locations, ...locationsOfOrganization(organization), ...locationsOfWorkspace(workspace)].filter(Boolean);
  const selectedLocation = organizationLocationId
    ? attachedLocations.find((location) => String(location.id || "") === String(organizationLocationId))
    : null;
  if (selectedLocation) return locationFromInfo(selectedLocation);
  const choices = [...attachedLocations, organization.locationInformation, organization.location, organization.address].filter(Boolean);
  let firstDisplayLocation = null;
  for (const item of choices) {
    const normalized = locationFromInfo(item);
    if (!normalized) continue;
    if (hasCoordinates(normalized)) return normalized;
    if (!firstDisplayLocation) firstDisplayLocation = normalized;
  }
  return firstDisplayLocation;
};
const distanceMiles = (first, second) => {
  const lat1Value = Number(first?.latitude);
  const lon1Value = Number(first?.longitude);
  const lat2Value = Number(second?.latitude);
  const lon2Value = Number(second?.longitude);
  if (![lat1Value, lon1Value, lat2Value, lon2Value].every(Number.isFinite)) return null;
  const earthRadiusMiles = 3958.7613;
  const lat1 = (lat1Value * Math.PI) / 180;
  const lat2 = (lat2Value * Math.PI) / 180;
  const deltaLat = ((lat2Value - lat1Value) * Math.PI) / 180;
  const deltaLon = ((lon2Value - lon1Value) * Math.PI) / 180;
  const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return Number((earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2));
};
const hasCoordinates = (location) =>
  Number.isFinite(Number(location?.latitude)) && Number.isFinite(Number(location?.longitude));
const acceptableDistanceText = () =>
  `${LOCATION_EXCEPTION_THRESHOLD_MILES.toFixed(2)} mile${LOCATION_EXCEPTION_THRESHOLD_MILES === 1 ? "" : "s"}`;
const isAwayFromFacility = (distance) =>
  distance != null && Number(distance) >= LOCATION_EXCEPTION_THRESHOLD_MILES;
const distanceStatusClass = (baseClassName, distance) =>
  `${baseClassName}${isAwayFromFacility(distance) ? " is-away-from-facility" : ""}`;
const distanceHelpText = ({ clockLocation, facilityLocation, distance }) => {
  if (distance != null) {
    const threshold = `Furthest acceptable distance: ${acceptableDistanceText()}.`;
    return isAwayFromFacility(distance)
      ? `${threshold} This will be submitted for supervisor review.`
      : `${threshold} No location exception reason required.`;
  }
  if (!hasCoordinates(clockLocation)) return "Current GPS coordinates were not captured.";
  if (!hasCoordinates(facilityLocation)) return "Facility coordinates are missing for the selected organization location.";
  return "Distance could not be calculated.";
};
const staffNameFor = (record = {}, profilesById = {}) => {
  const profile = profilesById[record.careProfileId] || profilesById[record.caregiverUserId];
  return clean(record.caregiverName) || (profile ? nameFor(profile) : "") || clean(record.caregiverEmail) || "Care staff";
};
const staffEmailFor = (record = {}, profilesById = {}) => {
  const profile = profilesById[record.careProfileId] || profilesById[record.caregiverUserId];
  return clean(record.caregiverEmail) || (profile ? emailFor(profile) : "");
};
const locationPermissionMessage = () =>
  [
    "Location access is required for clock in and clock out.",
    "Allow location for this site from the browser prompt, and make sure Location Services are enabled for this browser.",
  ].join(" ");
const getBrowserPosition = () =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("This browser does not expose location access."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  });
const reverseGeocode = async ({ latitude, longitude, apiKey }) => {
  try {
    const ready = await ensureGoogleMapsPlacesLoaded(apiKey);
    if (!ready || !window.google?.maps?.Geocoder) return {};
    const geocoder = new window.google.maps.Geocoder();
    const result = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
    const place = result?.results?.[0];
    if (!place) return {};
    return {
      ...addressFieldsFromPlaceDetails(place),
      formattedAddress: place.formatted_address || "",
      address: place.formatted_address || "",
      placeId: place.place_id || "",
    };
  } catch {
    return {};
  }
};
const captureCurrentLocation = async ({ actorId, apiKey }) => {
  const position = await getBrowserPosition();
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  const address = await reverseGeocode({ latitude, longitude, apiKey });
  return {
    latitude,
    longitude,
    address: address.formattedAddress || address.address || address.addressLine1 || "",
  };
};
const locationText = (location = {}) =>
  clean(location.address) ||
  [location.addressLine1, location.addressLine2, location.city, location.state, location.zipCode, location.country].map(clean).filter(Boolean).join(", ") ||
  clean(location.name) ||
  [location.latitude, location.longitude].map(clean).filter(Boolean).join(", ");
const coordinateText = (location = {}) => {
  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};
const LocationSummary = ({ location, details = false }) => {
  if (!location) return <span className="attendance-location-muted">Location will be captured automatically.</span>;
  const address = locationText(location);
  const coords = coordinateText(location);
  const placeId = clean(location.placeId || location.place_id);
  return (
    <span className="attendance-location-summary">
      <strong>{address || location.name || (details ? "Facility location" : "Current location")}</strong>
      {details && location.name && address && address !== location.name ? <small>{location.name}</small> : null}
      {coords ? <small>{coords}</small> : null}
      {!details && placeId ? <small>Place ID: {placeId}</small> : null}
    </span>
  );
};
const attendancePhotoId = (record = {}, type = "clockIn") => {
  if (type === "clockOut") {
    return record.clockOutPhoto || record.clockOutPhotoId || record.clockOutFileId || "";
  }
  return record.clockInPhoto || record.clockInPhotoId || record.clockInFileId || "";
};
const attendancePhotoUrl = (record = {}, type = "clockIn") => {
  if (type === "clockOut") {
    return record.clockOutPhotoUrl || record.clockOutSignedUrl || "";
  }
  return record.clockInPhotoUrl || record.clockInSignedUrl || "";
};
const locationReviewText = (record = {}) => {
  if (!record.locationExceptionRequired) return "Not required";
  const status = statusLabel(record.locationApprovalStatus || "PENDING");
  const distance = Number(record.distanceFromFacilityMiles);
  return `${status}${Number.isFinite(distance) ? ` - ${distance.toFixed(2)} mi` : ""}`;
};
const clockInTimestampFor = (record = {}) =>
  record.clockInTimestamp ||
  record.clockInTimeStamp ||
  record.clockInAt ||
  record.clockedInAt ||
  record.clockIn ||
  record.clockInDateTime ||
  record.clockInDatetime ||
  record.clockInDate ||
  record.clockInTime ||
  record.startTimestamp ||
  record.startedAt ||
  record.createdAt ||
  "";
const dateKeyFromTimestamp = (value) => {
  const ms = timestampMs(value);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toISOString().slice(0, 10);
};
const parseDateKey = (value) => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : new Date();
};
const monthKeyFromDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (date) => date.toLocaleDateString([], { month: "long", year: "numeric" });
const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);
const calendarDaysForMonth = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      key: `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`,
      inMonth: day.getMonth() === monthDate.getMonth(),
    };
  });
};
const fileFromDataUrl = async (dataUrl, filename) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
};
const dataUrlByteSize = (dataUrl = "") => {
  const base64 = String(dataUrl).split(",")[1] || "";
  return Math.ceil((base64.length * 3) / 4);
};
const compressedClockPhotoDataUrl = (sourceCanvas) => {
  let width = sourceCanvas.width;
  let height = sourceCanvas.height;
  let quality = 0.7;
  let dataUrl = sourceCanvas.toDataURL("image/jpeg", quality);

  while (dataUrlByteSize(dataUrl) > CLOCK_PHOTO_MAX_BYTES && (quality > 0.25 || width > 360)) {
    if (quality > 0.25) {
      quality = Math.max(0.25, quality - 0.1);
    } else {
      width = Math.max(320, Math.floor(width * 0.82));
      height = Math.max(240, Math.floor(height * 0.82));
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) break;
    context.drawImage(sourceCanvas, 0, 0, width, height);
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }

  return dataUrl;
};

export default function CareStaffAttendancePage({ scope = "workspace", mode = "clock" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { appConfig } = useAppConfig();
  const { uploadFile, uploading: photoUploading } = useFileUpload();
  const actorId = currentUserId(user);
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const isMine = scope === "mine";
  const recordsOnly = mode === "records";
  const [careProfile, setCareProfile] = useState(null);
  const [careProfiles, setCareProfiles] = useState([]);
  const [records, setRecords] = useState([]);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [rangeStart, setRangeStart] = useState(() => daysAgo(29));
  const [rangeEnd, setRangeEnd] = useState(() => today());
  const [staffFilter, setStaffFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [attendanceView, setAttendanceView] = useState("table");
  const [calendarMonth, setCalendarMonth] = useState(() => parseDateKey(today()));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [printedAtLabel, setPrintedAtLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [locationReason, setLocationReason] = useState("");
  const [facilityLocation, setFacilityLocation] = useState(null);
  const [clockConfirm, setClockConfirm] = useState(null);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [reviewRecord, setReviewRecord] = useState(null);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [reviewDecision, setReviewDecision] = useState("APPROVED");
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [autoLocationLoading, setAutoLocationLoading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [locationPreview, setLocationPreview] = useState(null);
  const [clockPhotoPreview, setClockPhotoPreview] = useState("");
  const [attendancePhotoPreview, setAttendancePhotoPreview] = useState(null);
  const [attendanceDetailsLoading, setAttendanceDetailsLoading] = useState(false);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  const loadData = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    if (isMine) {
      careService.careProfiles.getMine({
        actorId,
        userId: actorId,
        ...workspaceParams,
      })
        .then((result) => {
          const profile = normalizeCareProfile(result);
          const profileId = profileIdFor(profile || {});
          setCareProfile(profile);
          setCareProfiles(profile ? [profile] : []);
          if (!profileId) {
            setRecords([]);
            setCurrentRecord(null);
            setError("No completed care profile found for your account.");
            return Promise.resolve();
          }
          return Promise.allSettled([
            careService.attendance.listForCareProfile(profileId, workspaceParams),
            careService.attendance.currentForCareProfile(profileId, workspaceParams),
          ]).then(([recordsResult, currentResult]) => {
            const nextRecords = recordsResult.status === "fulfilled" && Array.isArray(recordsResult.value) ? recordsResult.value : [];
            const currentFromEndpoint = currentResult.status === "fulfilled" ? currentResult.value : null;
            const currentFromRecords = nextRecords.find((record) => record.status === "CLOCKED_IN") || null;
            setRecords(nextRecords);
            setCurrentRecord(currentFromEndpoint || currentFromRecords);
            if (recordsResult.status === "rejected") {
              setError(recordsResult.reason?.response?.data?.message || recordsResult.reason?.message || "Unable to load ClockInOut records.");
            }
          });
        })
        .catch((loadError) => {
          setRecords([]);
          setCurrentRecord(null);
          setError(loadError?.response?.data?.message || loadError?.message || "Unable to load ClockInOut records.");
        })
        .finally(() => setLoading(false));
      return;
    }

    Promise.allSettled([
      careService.attendance.list(workspaceParams),
      careService.careProfiles.list(workspaceParams),
    ])
      .then(([recordsResult, profilesResult]) => {
        setRecords(recordsResult.status === "fulfilled" && Array.isArray(recordsResult.value) ? recordsResult.value : []);
        setCareProfiles(profilesResult.status === "fulfilled" && Array.isArray(profilesResult.value) ? profilesResult.value : []);
        if (recordsResult.status === "rejected") {
          setError(recordsResult.reason?.response?.data?.message || recordsResult.reason?.message || "Unable to load ClockInOut records.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, isMine, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    if (!isMine || recordsOnly) return undefined;
    let alive = true;
    setAutoLocationLoading(true);
    captureCurrentLocation({ actorId, apiKey: googleMapsApiKey })
      .then((location) => {
        if (alive) setLocationPreview(location);
      })
      .catch((locationError) => {
        if (!alive) return;
        const permissionDenied = locationError?.code === 1;
        setError((current) => current || (permissionDenied ? locationPermissionMessage() : locationError?.message || "Unable to capture current location."));
      })
      .finally(() => {
        if (alive) setAutoLocationLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [actorId, googleMapsApiKey, isMine, recordsOnly]);

  useEffect(() => {
    const organizationId = selectedWorkspace?.organizationId || selectedWorkspace?.businessEntityId || "";
    if (!organizationId) {
      setFacilityLocation(null);
      return undefined;
    }
    let alive = true;
    setFacilityLoading(true);
    Promise.allSettled([
      olreadiaService.organizations.get(organizationId),
      olreadiaService.organizations.locations.list(organizationId, { page: 0, size: 20 }),
    ])
      .then(([organizationResult, locationsResult]) => {
        if (!alive) return;
        const organization = organizationResult.status === "fulfilled" ? organizationResult.value : {};
        const locationPage = locationsResult.status === "fulfilled" ? locationsResult.value : [];
        const locations = Array.isArray(locationPage?.content) ? locationPage.content : Array.isArray(locationPage) ? locationPage : [];
        setFacilityLocation(locationFromOrganization(organization, locations, selectedWorkspace?.organizationLocationId, selectedWorkspace));
      })
      .catch(() => {
        if (alive) setFacilityLocation(null);
      })
      .finally(() => {
        if (alive) setFacilityLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedWorkspace, selectedWorkspace?.businessEntityId, selectedWorkspace?.organizationId, selectedWorkspace?.organizationLocationId]);

  const profilesById = useMemo(() => {
    const lookup = {};
    careProfiles.forEach((profile) => {
      const profileId = profileIdFor(profile);
      const userId = userIdFor(profile);
      if (profileId) lookup[profileId] = profile;
      if (userId) lookup[userId] = profile;
    });
    return lookup;
  }, [careProfiles]);
  const staffChoices = useMemo(
    () => careProfiles
      .map((profile) => ({ id: profileIdFor(profile), userId: userIdFor(profile), name: nameFor(profile), email: emailFor(profile) }))
      .filter((item) => item.id)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [careProfiles],
  );
  const filteredRecords = useMemo(() => {
    return records
      .filter((record) => {
        const date = String(clockInTimestampFor(record) || "").slice(0, 10);
        if (rangeStart && date < rangeStart) return false;
        if (rangeEnd && date > rangeEnd) return false;
        if (statusFilter !== "ALL" && record.status !== statusFilter) return false;
        if (staffFilter !== "ALL") {
          const selectedStaff = staffChoices.find((staff) => staff.id === staffFilter);
          const ids = [record.careProfileId, record.caregiverUserId].map((value) => String(value || ""));
          if (!ids.includes(staffFilter) && (!selectedStaff?.userId || !ids.includes(String(selectedStaff.userId)))) return false;
        }
        return true;
      })
      .sort((left, right) => String(right.clockInTimestamp || "").localeCompare(String(left.clockInTimestamp || "")));
  }, [rangeEnd, rangeStart, records, staffChoices, staffFilter, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRecords.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredRecords, pageSize]);
  const paginationStart = filteredRecords.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(filteredRecords.length, currentPage * pageSize);
  const totalHours = useMemo(
    () => filteredRecords.reduce((sum, record) => sum + Number(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp)), 0),
    [filteredRecords],
  );
  const recordsByDate = useMemo(() => {
    return filteredRecords.reduce((groups, record) => {
      const key = dateKeyFromTimestamp(clockInTimestampFor(record));
      if (!key) return groups;
      groups[key] = groups[key] || [];
      groups[key].push(record);
      return groups;
    }, {});
  }, [filteredRecords]);
  const calendarDays = useMemo(() => calendarDaysForMonth(calendarMonth), [calendarMonth]);
  const calendarMonthRecords = useMemo(
    () => filteredRecords.filter((record) => dateKeyFromTimestamp(clockInTimestampFor(record)).startsWith(monthKeyFromDate(calendarMonth))),
    [calendarMonth, filteredRecords],
  );
  const boardGroups = useMemo(() => {
    return Object.entries(recordsByDate)
      .sort(([left], [right]) => right.localeCompare(left))
      .map(([date, items]) => ({
        date,
        records: [...items].sort((left, right) => String(clockInTimestampFor(right) || "").localeCompare(String(clockInTimestampFor(left) || ""))),
      }));
  }, [recordsByDate]);
  const recentTwoDayRecords = useMemo(() => {
    const start = daysAgo(1);
    return records
      .filter((record) => {
        const date = String(clockInTimestampFor(record) || "").slice(0, 10);
        return date && date >= start;
      })
      .sort((left, right) => String(clockInTimestampFor(right) || "").localeCompare(String(clockInTimestampFor(left) || "")))
      .slice(0, 6);
  }, [records]);
  const clockedInCount = useMemo(
    () => records.filter((record) => record.status === "CLOCKED_IN").length,
    [records],
  );
  const myCareProfileId = profileIdFor(careProfile || {});
  const facilityBusinessEntityId = selectedWorkspace?.organizationId || selectedWorkspace?.businessEntityId || "";
  const dashboardLocation = currentRecord?.clockInLocation || locationPreview;
  const dashboardDistance = distanceMiles(dashboardLocation, facilityLocation);
  const clockConfirmActionText = clockConfirm?.action === "clock-out" ? "Clock Out" : "Clock In";
  const clockConfirmOkText = clockPhotoPreview ? clockConfirmActionText : `Take Photo & ${clockConfirmActionText}`;

  useEffect(() => {
    setPage(1);
  }, [rangeStart, rangeEnd, staffFilter, statusFilter, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const clearPrintMode = () => document.body.classList.remove("printing-attendance");
    window.addEventListener("afterprint", clearPrintMode);
    return () => {
      window.removeEventListener("afterprint", clearPrintMode);
      clearPrintMode();
    };
  }, []);

  const exportAttendanceRecordsToExcel = () => {
    if (typeof window === "undefined" || !filteredRecords.length) return;

    const rows = filteredRecords.map((record) => [
      staffNameFor(record, profilesById),
      staffEmailFor(record, profilesById),
      formatTimestamp(clockInTimestampFor(record)),
      formatTimestamp(record.clockOutTimestamp),
      locationText(record.clockOutLocation || record.clockInLocation),
      formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp)),
      statusLabel(record.status),
      locationReviewText(record),
      [record.clockInNotes, record.clockOutNotes].map(clean).filter(Boolean).join(" / "),
    ]);
    const html = `<!doctype html>
<html>
<head><meta charset="utf-8" /></head>
<body>
  <table>
    <thead>
      <tr><th>Staff</th><th>Email</th><th>Clock in</th><th>Clock out</th><th>Location</th><th>Hours</th><th>Status</th><th>Location review</th><th>Notes</th></tr>
    </thead>
    <tbody>
      ${rows.map((cells) => `<tr>${cells.map((cell) => `<td>${excelCell(cell)}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance-${filenameDate()}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printAttendanceRecords = () => {
    if (typeof window === "undefined") return;
    setPrintedAtLabel(new Date().toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }));
    document.body.classList.add("printing-attendance");
    window.setTimeout(() => window.print(), 50);
  };

  const buildAttendancePayload = async () => {
    const location = await captureCurrentLocation({ actorId, apiKey: googleMapsApiKey });
    const distance = distanceMiles(location, facilityLocation);
    setLocationPreview(location);
    return {
      payload: {
        careProfileId: myCareProfileId,
        workspaceId: selectedWorkspaceId,
        facilityBusinessEntityId,
        notes,
        location,
        facilityLocation,
        locationReason: clean(locationReason) || null,
      },
      distance,
    };
  };

  const doClockIn = async () => {
    if (!myCareProfileId) {
      setError("No care profile found for your account.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const next = await buildAttendancePayload();
      setLocationReason("");
      setNotes("");
      setClockPhotoPreview("");
      setClockConfirm({ action: "clock-in", ...next });
    } catch (saveError) {
      const permissionDenied = saveError?.code === 1;
      setError(saveError?.response?.data?.message || (permissionDenied ? locationPermissionMessage() : saveError?.message) || "Unable to clock in.");
    } finally {
      setSaving(false);
    }
  };

  const doClockOut = async () => {
    if (!currentRecord?.id) {
      setError("No active clock-in record found.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const next = await buildAttendancePayload();
      setLocationReason("");
      setNotes("");
      setClockPhotoPreview("");
      setClockConfirm({ action: "clock-out", ...next });
    } catch (saveError) {
      const permissionDenied = saveError?.code === 1;
      setError(saveError?.response?.data?.message || (permissionDenied ? locationPermissionMessage() : saveError?.message) || "Unable to clock out.");
    } finally {
      setSaving(false);
    }
  };

  const confirmClockAction = async () => {
    if (!clockConfirm?.payload) return;
    const needsReason = Number(clockConfirm.distance) >= LOCATION_EXCEPTION_THRESHOLD_MILES;
    if (needsReason && !clean(locationReason)) {
      setError(`You are ${Number(clockConfirm.distance).toFixed(2)} miles from the facility. Add a location exception reason.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const photoPreview = clockPhotoPreview || captureClockPhotoDataUrl();
      if (!photoPreview) return;
      const clockPhotoFile = await fileFromDataUrl(
        photoPreview,
        `${clockConfirm.action}-${Date.now()}.jpg`,
      );
      const upload = await uploadFile(actorId, clockPhotoFile, {
        purpose: "OTHER",
        fileType: "PHOTO",
        skipUserValidation: true,
        confirmUpload: false,
        preserveOriginal: false,
      });
      const photo = upload?.fileId || upload?.file?.id || null;
      const payload = {
        ...clockConfirm.payload,
        notes,
        photo,
        locationReason: needsReason ? clean(locationReason) : clean(locationReason) || null,
      };
      const saved = clockConfirm.action === "clock-out"
        ? await careService.attendance.clockOut(currentRecord.id, payload, workspaceParams)
        : await careService.attendance.clockIn(payload, workspaceParams);
      if (clockConfirm.action === "clock-out") {
        setCurrentRecord(null);
        setRecords((current) => [saved, ...current.filter((record) => record.id !== saved.id)]);
      } else {
        setCurrentRecord(saved);
        setRecords((current) => [saved, ...current]);
      }
      setNotes("");
      setLocationReason("");
      setClockPhotoPreview("");
      setClockConfirm(null);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save ClockInOut record.");
    } finally {
      setSaving(false);
    }
  };

  const stopClockCamera = () => {
    cameraStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
  };

  const captureClockPhotoDataUrl = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError("Camera is still starting. Try again in a moment.");
      return "";
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Unable to take a camera photo in this browser.");
      return "";
    }
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = compressedClockPhotoDataUrl(canvas);
    setClockPhotoPreview(dataUrl);
    setCameraError("");
    return dataUrl;
  };

  const captureClockPhoto = () => {
    captureClockPhotoDataUrl();
  };

  const retakeClockPhoto = () => {
    setClockPhotoPreview("");
    setCameraError("");
  };

  const closeClockConfirm = () => {
    stopClockCamera();
    setClockConfirm(null);
    setClockPhotoPreview("");
    setCameraError("");
  };

  useEffect(() => {
    if (!clockConfirm) {
      stopClockCamera();
      return undefined;
    }
    let alive = true;
    setCameraLoading(true);
    setCameraError("");
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setCameraLoading(false);
      setCameraError("This browser does not expose camera access.");
      return undefined;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (!alive) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play?.().catch(() => {});
        }
      })
      .catch((cameraAccessError) => {
        if (alive) setCameraError(cameraAccessError?.message || "Unable to open the camera.");
      })
      .finally(() => {
        if (alive) setCameraLoading(false);
      });
    return () => {
      alive = false;
      stopClockCamera();
    };
  }, [clockConfirm]);

  useEffect(() => {
    return () => {
      stopClockCamera();
    };
  }, []);

  useEffect(() => {
    if (!clockConfirm) {
      setClockPhotoPreview("");
      setCameraError("");
      setCameraLoading(false);
    }
  }, [clockConfirm]);

  useEffect(() => {
    if (!selectedAttendanceRecord?.id) return undefined;
    let alive = true;
    setAttendanceDetailsLoading(true);
    careService.attendance.get(selectedAttendanceRecord.id, workspaceParams)
      .then((details) => {
        if (alive && details?.id) setSelectedAttendanceRecord(details);
      })
      .catch((detailsError) => {
        if (alive) setError(detailsError?.response?.data?.message || detailsError?.message || "Unable to load ClockInOut details.");
      })
      .finally(() => {
        if (alive) setAttendanceDetailsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedAttendanceRecord?.id, workspaceParams]);

  const openReview = (record, decision = "APPROVED") => {
    setReviewRecord(record);
    setReviewDecision(decision);
    setReviewNotes(record.supervisorLocationReviewNotes || "");
  };

  const saveLocationReview = async () => {
    if (!reviewRecord?.id) return;
    setReviewSaving(true);
    setError("");
    try {
      const saved = await careService.attendance.reviewLocation(reviewRecord.id, {
        actorId,
        locationApprovalStatus: reviewDecision,
        supervisorLocationReviewNotes: reviewNotes,
      }, workspaceParams);
      setRecords((current) => current.map((record) => (record.id === saved.id ? saved : record)));
      setReviewRecord(null);
      setReviewNotes("");
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to review location exception.");
    } finally {
      setReviewSaving(false);
    }
  };

  const renderAttendancePhotoPreview = (record, type, label) => {
    const photoId = attendancePhotoId(record, type);
    if (!photoId) return null;
    const url = attendancePhotoUrl(record, type);
    return (
      <button
        className="attendance-photo-preview-tile"
        type="button"
        onClick={() => url && setAttendancePhotoPreview({ url, label })}
        disabled={!url}
      >
        <span>{label}</span>
        {url ? (
          <img src={url} alt={`${label} preview`} />
        ) : (
          <small>{attendanceDetailsLoading ? "Loading photo..." : "Photo preview unavailable"}</small>
        )}
      </button>
    );
  };

  return (
    <CareShell className={`dashboard-content residents-page care-staff-attendance-page${isMine ? " my-attendance-page" : ""}${recordsOnly ? " attendance-records-page" : ""}`}>
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{isMine ? "My ClockInOut" : "Staff ClockInOut"}</h1>
          <p>{isMine ? (recordsOnly ? "Review your previous ClockInOut records." : "Clock in and clock out for your current shift.") : "Review ClockInOut records for care staff in this workspace."}</p>
        </div>
        <div className="care-staff-page-actions">
          {isMine && recordsOnly ? (
            <button className="mini-btn timesheet-nav-blue" type="button" onClick={() => navigate("/my-attendance")}>
              <i className="ti ti-arrow-left" /> Back to clock
            </button>
          ) : null}
          <button className="mini-btn timesheet-nav-blue" type="button" onClick={loadData}>
            <i className="ti ti-refresh" /> Refresh
          </button>
          {(!isMine || recordsOnly) ? (
            <>
              <button className="mini-btn timesheet-export-excel-button" type="button" onClick={exportAttendanceRecordsToExcel} disabled={!filteredRecords.length}>
                <i className="ti ti-file-spreadsheet" /> Export Excel
              </button>
              <button className="mini-btn" type="button" onClick={printAttendanceRecords} disabled={!filteredRecords.length}>
                <i className="ti ti-printer" /> Print records
              </button>
            </>
          ) : null}
        </div>
      </div>

      {loading ? <Panel title="ClockInOut"><p>Loading ClockInOut records...</p></Panel> : null}
      {error ? <Panel title="ClockInOut"><p className="form-error">{error}</p></Panel> : null}

      {isMine && !recordsOnly ? (
        <section className="attendance-dash">
          <div className="attendance-dash-hero">
            <span className="attendance-dash-kicker">ClockInOut clock</span>
            <h2>{currentRecord ? "You are clocked in" : "Ready to clock in"}</h2>
            <strong>
              {currentRecord && clockInTimestampFor(currentRecord) ? (
                <AttendanceElapsedTimer startedAt={clockInTimestampFor(currentRecord)} />
              ) : "00:00:00"}
            </strong>
            <p>{currentRecord ? `Clocked In At ${formatTimestamp(clockInTimestampFor(currentRecord))}` : "Clock in first. Clock out unlocks after your shift has started."}</p>
          </div>

          <div className="attendance-action-grid">
            <button
              className={`attendance-action-card is-clock-in${currentRecord ? " is-disabled" : " is-active"}`}
              type="button"
              onClick={doClockIn}
              disabled={saving || !myCareProfileId || Boolean(currentRecord)}
            >
              <i className="ti ti-clock-play" />
              <span>Clock In</span>
              <small>{currentRecord ? "Already clocked in" : "Start your shift"}</small>
            </button>
            <button
              className={`attendance-action-card is-clock-out${currentRecord ? " is-active" : " is-disabled"}`}
              type="button"
              onClick={doClockOut}
              disabled={saving || !myCareProfileId || !currentRecord}
            >
              <i className="ti ti-clock-off" />
              <span>Clock Out</span>
              <small>{currentRecord ? "End your shift" : "Available after clock in"}</small>
            </button>
          </div>

          <div className="attendance-clock-card">
            <div className="attendance-clock-location">
              <span>{currentRecord ? "Clock-in location" : "Automatic location"}</span>
              {autoLocationLoading && !locationPreview && !currentRecord?.clockInLocation ? (
                <span className="attendance-location-muted">Capturing current location...</span>
              ) : (
                <LocationSummary location={currentRecord?.clockInLocation || locationPreview} />
              )}
            </div>
            <div className="attendance-clock-location">
              <span>Facility location</span>
              {facilityLoading ? <span className="attendance-location-muted">Loading facility location...</span> : <LocationSummary location={facilityLocation} details />}
            </div>
            <div className={distanceStatusClass("attendance-clock-location", dashboardDistance)}>
              <span>Distance from facility</span>
              <strong>{dashboardDistance == null ? "Calculating..." : `${dashboardDistance.toFixed(2)} miles`}</strong>
              <small>
                Furthest acceptable distance: {acceptableDistanceText()}.{" "}
                {isAwayFromFacility(dashboardDistance) ? "Reason required when saving" : "Within normal clock range"}
              </small>
            </div>
          </div>

          <section className="attendance-recent-card">
            <div className="attendance-recent-head">
              <div>
                <span>Recent ClockInOut</span>
                <strong>Last 2 days</strong>
              </div>
              <button className="mini-btn timesheet-nav-blue" type="button" onClick={() => navigate("/my-attendance/records")}>
                <i className="ti ti-list-details" /> More ClockInOut records
              </button>
            </div>
            {recentTwoDayRecords.length ? (
              <div className="attendance-recent-list">
                {recentTwoDayRecords.map((record) => (
                  <article key={record.id} role="button" tabIndex={0} onClick={() => setSelectedAttendanceRecord(record)} onKeyDown={(event) => event.key === "Enter" && setSelectedAttendanceRecord(record)}>
                    <i className={`ti ${record.status === "CLOCKED_IN" ? "ti-clock-play" : "ti-clock-check"}`} />
                    <div>
                      <strong>{statusLabel(record.status)}</strong>
                      <span>{formatTimestamp(clockInTimestampFor(record))}</span>
                    </div>
                    <b>{formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp))}</b>
                  </article>
                ))}
              </div>
            ) : (
              <div className="attendance-recent-empty">
                <i className="ti ti-calendar-time" />
                <span>No ClockInOut records in the last 2 days.</span>
              </div>
            )}
          </section>
        </section>
      ) : null}

      {(!isMine || recordsOnly) ? (
      <>
      <section className="timesheet-summary-grid attendance-summary-grid">
        <article>
          <span>Total hours in range</span>
          <strong>{formatHours(totalHours)}</strong>
          <small>{filteredRecords.length} record{filteredRecords.length === 1 ? "" : "s"}</small>
        </article>
        <article>
          <span>Currently clocked in</span>
          <strong>{clockedInCount}</strong>
          <small>Active ClockInOut records</small>
        </article>
        <label>
          Range start
          <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
        </label>
        <label>
          Range end
          <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
        </label>
      </section>

      <section className={`timesheet-filter-bar${isMine ? " is-personal" : ""}`}>
        {!isMine ? (
          <label>
            Staff
            <Select
              showSearch
              value={staffFilter}
              optionFilterProp="searchText"
              onChange={(value) => setStaffFilter(value || "ALL")}
              options={[
                { value: "ALL", searchText: "All staff", label: "All staff" },
                ...staffChoices.map((staff) => ({
                  value: staff.id,
                  searchText: `${staff.name} ${staff.email}`,
                  label: (
                    <span className="timesheet-staff-option">
                      <strong>{staff.name}</strong>
                      <small>{staff.email || "No email"}</small>
                    </span>
                  ),
                })),
              ]}
            />
          </label>
        ) : null}
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ALL">All statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option value={status} key={status}>{statusLabel(status)}</option>
            ))}
          </select>
        </label>
      </section>

      <section className="attendance-print-area" aria-hidden="true">
        <header>
          <div>
            <h1>{isMine ? "My ClockInOut Records" : "Staff ClockInOut Records"}</h1>
            <p>{isMine ? "Review your previous ClockInOut records." : "Review ClockInOut records for care staff in this workspace."}</p>
          </div>
          <aside>
            <span>Printed</span>
            <strong>{printedAtLabel}</strong>
          </aside>
        </header>
        <div className="timesheet-print-meta">
          <span>Range: <strong>{rangeStart || "Any"} - {rangeEnd || "Any"}</strong></span>
          <span>Staff: <strong>{staffFilter === "ALL" ? "All staff" : staffChoices.find((staff) => staff.id === staffFilter)?.name || "Selected staff"}</strong></span>
          <span>Status: <strong>{statusFilter === "ALL" ? "All statuses" : statusLabel(statusFilter)}</strong></span>
          <span>Total: <strong>{formatHours(totalHours)}</strong></span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Staff</th>
              <th>Clock in</th>
              <th>Clock out</th>
              <th>Location</th>
              <th>Hours</th>
              <th>Status</th>
              <th>Location review</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length ? filteredRecords.map((record) => (
              <tr key={`print-${record.id || clockInTimestampFor(record)}`}>
                <td>
                  <strong>{staffNameFor(record, profilesById)}</strong>
                  <small>{staffEmailFor(record, profilesById)}</small>
                </td>
                <td>{formatTimestamp(clockInTimestampFor(record))}</td>
                <td>{formatTimestamp(record.clockOutTimestamp)}</td>
                <td>{locationText(record.clockOutLocation || record.clockInLocation)}</td>
                <td>{formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp))}</td>
                <td>{statusLabel(record.status)}</td>
                <td>{locationReviewText(record)}</td>
                <td>{[record.clockInNotes, record.clockOutNotes].map(clean).filter(Boolean).join(" / ")}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8}>No ClockInOut records found for this range.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <Panel
        title="ClockInOut Records"
        actionNode={(
          <div className="attendance-view-switch" aria-label="Attendance view">
            {[
              ["table", "Table", "ti-list-details"],
              ["calendar", "Calendar", "ti-calendar"],
              ["board", "Board", "ti-layout-kanban"],
            ].map(([view, label, icon]) => (
              <button
                className={attendanceView === view ? "is-active" : ""}
                key={view}
                onClick={() => setAttendanceView(view)}
                type="button"
              >
                <i className={`ti ${icon}`} /> {label}
              </button>
            ))}
          </div>
        )}
      >
        {attendanceView === "table" ? (
        <div className="attendance-table">
          <div className="attendance-table-row is-head">
            <span>Staff</span>
            <span>Clock in</span>
            <span>Clock out</span>
            <span>Location</span>
            <span>Hours</span>
            <span>Status</span>
            <span>Location review</span>
            <span>Notes</span>
            <span>Actions</span>
          </div>
          {paginatedRecords.length ? paginatedRecords.map((record) => (
            <div className="attendance-table-row" key={record.id} role="button" tabIndex={0} onClick={() => setSelectedAttendanceRecord(record)} onKeyDown={(event) => event.key === "Enter" && setSelectedAttendanceRecord(record)}>
              <span className="timesheet-staff-cell">
                <strong>{staffNameFor(record, profilesById)}</strong>
                <small>{staffEmailFor(record, profilesById)}</small>
              </span>
              <span>{formatTimestamp(clockInTimestampFor(record))}</span>
              <span>{formatTimestamp(record.clockOutTimestamp)}</span>
              <LocationSummary location={record.clockOutLocation || record.clockInLocation} />
              <b>{formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp))}</b>
              <span><em className={`attendance-status is-${String(record.status || "").toLowerCase().replace(/_/g, "-")}`}>{statusLabel(record.status)}</em></span>
              <span>
                <em className={`attendance-review-status is-${String(record.locationApprovalStatus || "not_required").toLowerCase().replace(/_/g, "-")}`}>
                  {locationReviewText(record)}
                </em>
                {record.caregiverLocationReason ? <small className="attendance-review-reason">{record.caregiverLocationReason}</small> : null}
              </span>
              <span className="attendance-notes">
                {[record.clockInNotes, record.clockOutNotes].map(clean).filter(Boolean).join(" / ") || "No notes"}
              </span>
              <span className="attendance-actions">
                {!isMine && record.locationExceptionRequired ? (
                  <button className="mini-btn timesheet-nav-blue" type="button" onClick={(event) => { event.stopPropagation(); openReview(record); }}>
                    <i className="ti ti-clipboard-check" /> Review
                  </button>
                ) : (
                  <span className="attendance-location-muted">None</span>
                )}
              </span>
            </div>
          )) : (
            <div className="timesheet-empty">
              <i className="ti ti-clock" />
              <span>No ClockInOut records found for this range.</span>
            </div>
          )}
        </div>
        ) : null}

        {attendanceView === "calendar" ? (
          <div className="attendance-calendar-view">
            <div className="attendance-calendar-toolbar">
              <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, -1))}>
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <div>
                <strong>{monthLabel(calendarMonth)}</strong>
                <small>{calendarMonthRecords.length} ClockInOut record{calendarMonthRecords.length === 1 ? "" : "s"}</small>
              </div>
              <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, 1))}>
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
            <div className="attendance-calendar-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => <span key={label}>{label}</span>)}
            </div>
            <div className="attendance-calendar-grid">
              {calendarDays.map((day) => {
                const dayRecords = recordsByDate[day.key] || [];
                const activeCount = dayRecords.filter((record) => record.status === "CLOCKED_IN").length;
                return (
                  <div className={`attendance-calendar-day${day.inMonth ? "" : " is-muted"}`} key={day.key}>
                    <header>
                      <strong>{day.date.getDate()}</strong>
                      {dayRecords.length ? <em>{dayRecords.length}</em> : null}
                    </header>
                    <div className="attendance-calendar-day-list">
                      {dayRecords.slice(0, 4).map((record) => (
                        <button type="button" key={record.id || `${day.key}-${staffNameFor(record, profilesById)}`} onClick={() => setSelectedAttendanceRecord(record)}>
                          <span>{staffNameFor(record, profilesById)}</span>
                          <small>{formatTimeLabel(clockInTimestampFor(record))}</small>
                        </button>
                      ))}
                      {dayRecords.length > 4 ? <small>+{dayRecords.length - 4} more</small> : null}
                    </div>
                    {activeCount ? <b>{activeCount} clocked in</b> : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {attendanceView === "board" ? (
          <div className="attendance-board-view">
            {boardGroups.length ? boardGroups.map((group) => {
              const activeCount = group.records.filter((record) => record.status === "CLOCKED_IN").length;
              return (
                <section className="attendance-board-column" key={group.date}>
                  <header>
                    <div>
                      <strong>{formatDateLabel(group.date)}</strong>
                      <small>{group.records.length} record{group.records.length === 1 ? "" : "s"} · {activeCount} clocked in</small>
                    </div>
                  </header>
                  <div>
                    {group.records.map((record) => (
                      <button className="attendance-board-card" key={record.id || `${group.date}-${staffNameFor(record, profilesById)}`} type="button" onClick={() => setSelectedAttendanceRecord(record)}>
                        <span><i className={`ti ${record.status === "CLOCKED_IN" ? "ti-clock-play" : "ti-clock-check"}`} /> {statusLabel(record.status)}</span>
                        <strong>{staffNameFor(record, profilesById)}</strong>
                        <small>{formatTimestamp(clockInTimestampFor(record))}</small>
                        <b>{formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), record.clockOutTimestamp))}</b>
                      </button>
                    ))}
                  </div>
                </section>
              );
            }) : (
              <div className="timesheet-empty">
                <i className="ti ti-layout-kanban" />
                <span>No ClockInOut records found for this range.</span>
              </div>
            )}
          </div>
        ) : null}

        {filteredRecords.length ? (
          <div className={`timesheet-pagination attendance-pagination${attendanceView === "table" ? "" : " is-hidden"}`}>
            <span>
              Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{filteredRecords.length}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {ATTENDANCE_PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage <= 1}
              >
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>
      </>
      ) : null}

      <Modal
        title={(
          <span className="attendance-clock-confirm-title">
            <i className={clockConfirm?.action === "clock-out" ? "ti ti-clock-off" : "ti ti-clock-check"} />
            {clockConfirm?.action === "clock-out" ? "Confirm Clock Out" : "Confirm Clock In"}
          </span>
        )}
        open={Boolean(clockConfirm)}
        onCancel={closeClockConfirm}
        onOk={confirmClockAction}
        okText={clockConfirmOkText}
        confirmLoading={saving || photoUploading}
        className="attendance-clock-confirm-modal"
        width={860}
        centered
      >
        {clockConfirm ? (
          <div className="attendance-clock-confirm">
            <section className="attendance-clock-location-card">
              <span>Current location</span>
              <LocationSummary location={clockConfirm.payload?.location} />
            </section>
            <section className="attendance-clock-location-card">
              <span>Facility location</span>
              <LocationSummary location={clockConfirm.payload?.facilityLocation} details />
            </section>
            <section className={distanceStatusClass("attendance-clock-distance-card", clockConfirm.distance)}>
              <span>Distance from facility</span>
              <strong>{clockConfirm.distance == null ? "Not available" : `${Number(clockConfirm.distance).toFixed(2)} miles`}</strong>
              <small>
                {distanceHelpText({
                  clockLocation: clockConfirm.payload?.location,
                  facilityLocation: clockConfirm.payload?.facilityLocation,
                  distance: clockConfirm.distance,
                })}
              </small>
            </section>
            <label className="attendance-clock-photo-card">
              Clock photo
              <div className="attendance-photo-capture">
                <video className={clockPhotoPreview ? "is-preview-hidden" : ""} ref={cameraVideoRef} autoPlay playsInline muted aria-label="Clock camera preview" />
                {!clockPhotoPreview ? (
                  <div className="attendance-selfie-frame" aria-hidden="true">
                    <span className="attendance-selfie-head" />
                    <span className="attendance-selfie-ear is-left" />
                    <span className="attendance-selfie-ear is-right" />
                    <span className="attendance-selfie-shoulders" />
                  </div>
                ) : null}
                {clockPhotoPreview ? <img src={clockPhotoPreview} alt="Clock capture preview" /> : null}
                {cameraLoading ? <span className="attendance-location-muted">Opening camera...</span> : null}
                {cameraError ? <span className="form-error">{cameraError}</span> : null}
                <div className="attendance-camera-actions">
                  <button className="mini-btn timesheet-nav-blue" type="button" onClick={clockPhotoPreview ? retakeClockPhoto : captureClockPhoto} disabled={cameraLoading}>
                    <i className="ti ti-camera" /> {clockPhotoPreview ? "Retake photo" : "Take photo"}
                  </button>
                </div>
                <small>Photo will be uploaded when you submit this ClockInOut record.</small>
              </div>
            </label>
            {isAwayFromFacility(clockConfirm.distance) ? (
              <label className="attendance-clock-notes-card attendance-clock-exception-card">
                Location exception reason
                <textarea value={locationReason} onChange={(event) => setLocationReason(event.target.value)} placeholder="Explain why you are clocking away from the facility" />
              </label>
            ) : null}
            <label className="attendance-clock-notes-card">
              Notes
              <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={clockConfirm.action === "clock-out" ? "Add notes for clock out" : "Add notes for clock in"} />
            </label>
          </div>
        ) : null}
      </Modal>

      <Drawer
        width={560}
        title="ClockInOut Details"
        open={Boolean(selectedAttendanceRecord)}
        onClose={() => setSelectedAttendanceRecord(null)}
        destroyOnClose
      >
        {selectedAttendanceRecord ? (
          <div className="attendance-details-drawer">
            <section>
              <span>Care staff</span>
              <strong>{staffNameFor(selectedAttendanceRecord, profilesById)}</strong>
              <small>{staffEmailFor(selectedAttendanceRecord, profilesById)}</small>
            </section>
            <section className="attendance-details-grid">
              <div>
                <span>Clock in</span>
                <strong>{formatTimestamp(clockInTimestampFor(selectedAttendanceRecord))}</strong>
              </div>
              <div>
                <span>Clock out</span>
                <strong>{formatTimestamp(selectedAttendanceRecord.clockOutTimestamp)}</strong>
              </div>
              <div>
                <span>Hours</span>
                <strong>{formatHours(selectedAttendanceRecord.hours ?? hoursBetween(clockInTimestampFor(selectedAttendanceRecord), selectedAttendanceRecord.clockOutTimestamp))}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{statusLabel(selectedAttendanceRecord.status)}</strong>
              </div>
            </section>
            {attendancePhotoId(selectedAttendanceRecord, "clockIn") || attendancePhotoId(selectedAttendanceRecord, "clockOut") ? (
              <section>
                <span>Clock photos</span>
                <div className="attendance-photo-preview-grid">
                  {renderAttendancePhotoPreview(selectedAttendanceRecord, "clockIn", "Clock-in photo")}
                  {renderAttendancePhotoPreview(selectedAttendanceRecord, "clockOut", "Clock-out photo")}
                </div>
              </section>
            ) : null}
            <section>
              <span>Clock-in location</span>
              <LocationSummary location={selectedAttendanceRecord.clockInLocation} />
            </section>
            <section>
              <span>Clock-out location</span>
              <LocationSummary location={selectedAttendanceRecord.clockOutLocation} />
            </section>
            <section>
              <span>Facility location</span>
              <LocationSummary location={selectedAttendanceRecord.facilityLocation} details />
            </section>
            <section className="attendance-details-grid">
              <div className={distanceStatusClass("", selectedAttendanceRecord.distanceFromFacilityMiles).trim() || undefined}>
                <span>Distance from facility</span>
                <strong>{selectedAttendanceRecord.distanceFromFacilityMiles == null ? "Not available" : `${Number(selectedAttendanceRecord.distanceFromFacilityMiles).toFixed(2)} miles`}</strong>
                <small>Furthest acceptable distance: {acceptableDistanceText()}</small>
              </div>
              <div>
                <span>Location review</span>
                <strong>{locationReviewText(selectedAttendanceRecord)}</strong>
              </div>
            </section>
            <section>
              <span>Notes</span>
              <p>{[selectedAttendanceRecord.clockInNotes, selectedAttendanceRecord.clockOutNotes].map(clean).filter(Boolean).join(" / ") || "No notes"}</p>
            </section>
            {selectedAttendanceRecord.caregiverLocationReason ? (
              <section>
                <span>Location exception reason</span>
                <p>{selectedAttendanceRecord.caregiverLocationReason}</p>
              </section>
            ) : null}
            {selectedAttendanceRecord.supervisorLocationReviewNotes ? (
              <section>
                <span>Supervisor notes</span>
                <p>{selectedAttendanceRecord.supervisorLocationReviewNotes}</p>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <Drawer
        width={520}
        title="Review Location Exception"
        open={Boolean(reviewRecord)}
        onClose={() => setReviewRecord(null)}
        destroyOnClose
      >
        {reviewRecord ? (
          <div className="attendance-review-drawer">
            <section>
              <span>Care staff</span>
              <strong>{staffNameFor(reviewRecord, profilesById)}</strong>
              <small>{staffEmailFor(reviewRecord, profilesById)}</small>
            </section>
            <section className={distanceStatusClass("", reviewRecord.distanceFromFacilityMiles).trim() || undefined}>
              <span>Distance from facility</span>
              <strong>{Number(reviewRecord.distanceFromFacilityMiles || 0).toFixed(2)} miles</strong>
              <small>Furthest acceptable distance: {acceptableDistanceText()}. Facility: {locationText(reviewRecord.facilityLocation) || "Not captured"}</small>
            </section>
            <section>
              <span>Clock location</span>
              <LocationSummary location={reviewRecord.clockOutLocation || reviewRecord.clockInLocation} />
            </section>
            <section>
              <span>Caregiver reason</span>
              <p>{reviewRecord.caregiverLocationReason || "No reason provided."}</p>
            </section>
            <label>
              Decision
              <select value={reviewDecision} onChange={(event) => setReviewDecision(event.target.value)}>
                <option value="APPROVED">Approve</option>
                <option value="DENIED">Deny</option>
              </select>
            </label>
            <label>
              Supervisor notes
              <textarea value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} placeholder="Add approval or denial notes" />
            </label>
            <div className="drawer-actions">
              <button className="mini-btn" type="button" onClick={() => setReviewRecord(null)}>Cancel</button>
              <button className="btn primary" type="button" onClick={saveLocationReview} disabled={reviewSaving}>
                <i className="ti ti-check" /> {reviewSaving ? "Saving..." : "Save review"}
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>

      <Modal
        title={attendancePhotoPreview?.label || "Clock photo"}
        open={Boolean(attendancePhotoPreview?.url)}
        onCancel={() => setAttendancePhotoPreview(null)}
        footer={null}
        className="attendance-photo-preview-modal"
      >
        {attendancePhotoPreview?.url ? (
          <img src={attendancePhotoPreview.url} alt={attendancePhotoPreview.label || "Clock photo preview"} />
        ) : null}
      </Modal>
    </CareShell>
  );
}
